import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Button,
  Box,
  CircularProgress,
  Alert,
  Snackbar,
  Stack,
  IconButton,
  Divider,
  Link,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { Link as RouterLink, useParams, useNavigate, useLocation } from 'react-router-dom';
import { fetchTutorProfile, fetchCourseRate } from '../services/api';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';

const localizer = momentLocalizer(moment);

const BookSessionPage = () => {
  const { rateId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const previousPath = location.state?.from || '/tutors';

  const [profile, setProfile] = useState(null);
  const [rate, setRate] = useState(null);
  const [events, setEvents] = useState([]);
  const [selectedTimes, setSelectedTimes] = useState([]);
  const [courseOffering, setCourseOffering] = useState(null);
  const [error, setError] = useState('');
  const [confirmedTimes, setConfirmedTimes] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const rateData = await fetchCourseRate(rateId);
        setRate(rateData);
        setCourseOffering(rateData.course_offering);
        const tutor = await fetchTutorProfile(rateData.tutor_id);
        setProfile(tutor);
        const availableEvents = tutor.availability_slots.map(slot => ({
          start: new Date(slot.start_time),
          end: new Date(slot.end_time),
          title: 'Available',
          type: 'available',
        }));
        setEvents(availableEvents);
      } catch (err) {
        setError('Failed to load data.');
      }
    };
    load();
  }, [rateId]);

  const eventPropGetter = (event) => {
    if (event.type === 'available') {
      return {
        style: {
          backgroundColor: '#d0f0c0',
          pointerEvents: 'none',
          opacity: 0.6,
        },
      };
    }
    if (event.type === 'selected') {
      return {
        style: {
          backgroundColor: '#2d2d52',
          color: 'white',
          border: '1px solid #2d2d52',
        },
      };
    }
    return {};
  };

  const handleSelectSlot = ({ start }) => {
    const durationMs = rate.duration_minutes * 60 * 1000;
    const end = new Date(start.getTime() + durationMs);

    const isInsideAvailable = events.some(e =>
      start >= e.start && end <= e.end
    );

    if (!isInsideAvailable) return;

    const isAlreadySelected = selectedTimes.some(t =>
      t.start.getTime() === start.getTime()
    );

    if (isAlreadySelected) {
      setSelectedTimes(prev => prev.filter(t => t.start.getTime() !== start.getTime()));
    } else {
      // Prevent overlaps
      const overlaps = selectedTimes.some(t =>
        (start < t.end && end > t.start)
      );
      if (overlaps) return;

      if (selectedTimes.length < rate.session_count) {
        setSelectedTimes(prev => [...prev, { start, end }]);
      }
    }
  };

  const total = rate ? rate.price * rate.session_count : 0;
  const tax = total * 0.085;
  const grandTotal = total + tax;

  if (!profile || !rate) return <CircularProgress />;

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Alert severity="info" sx={{ mb: 2 }}>
        The Tutoring page is under active development, please stay tuned, and
        feel free to explore, and give us suggestions through the{' '}
        <Link component={RouterLink} to="/feedback">
          feedback
        </Link>{' '}
        page
      </Alert>
      <Button
        variant="outlined"
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(previousPath)}
        sx={{ mb: 2 }}
      >
        Back
      </Button>

      <Typography variant="h4" gutterBottom>
        Book a Session
      </Typography>
      <Snackbar
        open={!!error}
        autoHideDuration={4000}
        onClose={() => setError("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setError("")}
          severity="error"
          sx={{ width: "100%" }}
        >
          {error}
        </Alert>
      </Snackbar>

      <Box sx={{ mt: 1, mb: 3, p: 2, bgcolor: 'grey.100', borderRadius: 2 }}>
        <Typography variant="h6" fontWeight="bold">{courseOffering.title}</Typography>
        <Typography variant="subtitle2" color="text.secondary">with {profile?.full_name}</Typography>
      </Box>

      {/* Rate Cards */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 3 }}>
      {Object.entries(
  courseOffering.rates.reduce((groups, pkg) => {
    const key = `${pkg.max_students}-${pkg.duration_minutes}`;
    if (!groups[key]) groups[key] = [];
    groups[key].push(pkg);
    return groups;
  }, {})
).map(([groupKey, rateGroup], index) => {
  const [max_students, duration_minutes] = groupKey.split('-');

  return (
    <Box key={groupKey} sx={{ width: '100%' }}>
      {index > 0 && <Divider sx={{ my: 2 }} />}
      <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
        1-on-{max_students} • {duration_minutes} minutes
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {rateGroup
          .sort((a, b) => a.session_count - b.session_count)
          .map(pkg => {
            const isSelected = pkg.id === rate.id;
            const perLesson = parseFloat(pkg.price).toFixed(2);
            const total = (pkg.session_count * parseFloat(pkg.price)).toFixed(2);
            const baseRate = courseOffering.rates.find(
              r => r.session_count === 1 && r.max_students === pkg.max_students && r.duration_minutes === pkg.duration_minutes
            );
            const baseTotal = baseRate ? baseRate.price * pkg.session_count : null;
            const discount = baseTotal ? Math.round((1 - total / baseTotal) * 100) : null;

            return (
              <Box key={pkg.id} onClick={() => setRate(pkg)} sx={{
                width: 'calc(33.333% - 16px)', minWidth: 200, p: 3,
                border: `2px solid ${isSelected ? '#2D2D52' : '#e0e0e0'}`,
                borderRadius: 2, boxShadow: isSelected ? 3 : 0, cursor: 'pointer',
                bgcolor: isSelected ? '#f7f9fc' : '#fff', transition: '0.2s ease-in-out',
              }}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" fontWeight="bold" color="text.secondary">
                    {pkg.session_count > 1 ? 'Package' : 'Single Session'}
                  </Typography>
                  {discount > 0 && (
                    <Typography variant="body2" color="primary" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                      <LocalOfferIcon fontSize="small" sx={{ mr: 0.5 }} />
                      {discount}% off
                    </Typography>
                  )}
                </Stack>
                <Typography variant="h5" fontWeight="bold" sx={{ mt: 1 }}>${perLesson} per lesson</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{pkg.duration_minutes} minutes</Typography>
                <Typography variant="body2" color="text.secondary">
                  {pkg.max_students === 1 ? '1-on-1' : `1-on-${pkg.max_students}`}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>{pkg.session_count} Lessons</Typography>
                <Box sx={{ mt: 0.5 }}>
                  {discount > 0 && baseTotal ? (
                    <Stack direction="row" spacing={1} alignItems="baseline">
                      <Typography
                        variant="body2"
                        sx={{ textDecoration: 'line-through', color: '#888' }}
                      >
                        ${baseTotal.toFixed(2)}
                      </Typography>
                      <Typography variant="h6" fontWeight="bold">
                        ${total}
                      </Typography>
                    </Stack>
                  ) : (
                    <Typography variant="h6" fontWeight="bold">
                      ${total}
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
      </Box>
    </Box>
  );
})}

      </Box>

      <Divider sx={{ my: 3 }} />

      {!confirmedTimes && (
        <>
          <Typography variant="h6">Select {rate.session_count} Time Slot(s)</Typography>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2, mb: 2 }}>
            <Box sx={{ width: 20, height: 20, bgcolor: '#d0f0c0', borderRadius: 1 }} />
            <Typography variant="body2">Available</Typography>
            <Box sx={{ width: 20, height: 20, bgcolor: '#2d2d52', borderRadius: 1 }} />
            <Typography variant="body2">Selected</Typography>
          </Stack>
          <Calendar
            selectable
            localizer={localizer}
            events={[...events, ...selectedTimes.map(t => ({
              start: t.start, end: t.end, title: 'Selected', type: 'selected',
            }))]}
            defaultView="week"
            views={['week', 'day']}
            onSelectSlot={handleSelectSlot}
            startAccessor="start"
            endAccessor="end"
            eventPropGetter={eventPropGetter}
            style={{ height: 500, marginTop: '1rem' }}
          />
          <Box textAlign="center" mt={4}>
            <Button
              variant="outlined"
              color="primary"
              disabled={selectedTimes.length !== rate.session_count}
              onClick={() => setConfirmedTimes(true)}
              sx={{
                color: (theme) => theme.palette.primary.main,
                borderColor: (theme) => theme.palette.primary.main,
                '&.Mui-disabled': {
                  color: (theme) => theme.palette.action.disabled,
                  borderColor: (theme) => theme.palette.action.disabledBackground,
                },
              }}
            >
              Confirm Time Selection
            </Button>
          </Box>
        </>
      )}

      <Box my={3}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle1">Selected Times:</Typography>
          {confirmedTimes && (
            <IconButton onClick={() => setConfirmedTimes(false)} aria-label="Edit Times">
              <EditIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
        {selectedTimes.length === 0 ? (
          <Typography color="text.secondary">No times selected.</Typography>
        ) : (
          <Stack spacing={1}>
            {selectedTimes.map((t, idx) => (
              <Box key={idx} sx={{ border: '1px solid #ccc', borderRadius: 1, px: 2, py: 1 }}>
                <Typography variant="body2">
                {t.start.toLocaleString(undefined, {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                })} — {t.start.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                })} to {t.end.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                    timeZoneName: 'short',
                })}
                </Typography>
              </Box>
            ))}
          </Stack>
        )}
      </Box>

      {/* Payment Summary */}
      {confirmedTimes && (
        <Box sx={{ mt: 5 }}>
          <Typography variant="h5" gutterBottom>Make Payment</Typography>
          <Typography><strong>Course:</strong> {courseOffering.title}</Typography>
          <Typography><strong>Rate:</strong> ${rate.price} per session</Typography>
          <Typography><strong>Subtotal:</strong> ${total.toFixed(2)}</Typography>
          <Typography><strong>Tax (8.5%):</strong> ${tax.toFixed(2)}</Typography>
          <Typography variant="h6" sx={{ mt: 1 }}>
            Total: ${grandTotal.toFixed(2)}
          </Typography>
          <Box sx={{ mt: 3 }}>
            <Button variant="contained" color="primary">
              Proceed to Payment
            </Button>
          </Box>
        </Box>
      )}
    </Container>
  );
};

export default BookSessionPage;
